// ============================================================================
// src/components/individualcomp/SubmissionsGrid.jsx
// ============================================================================

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { addVote, fetchCompetitionScores } from '@/services/apiService';
import Countdown from '@/components/ui/Countdown';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useModal } from '@/contexts/ModalContext';
import SubmissionCard from '@/components/competitions/SubmissionCard';

const GRID_ITEMS_PER_ROW = { sm: 2, md: 3, lg: 4, xl: 4 };

// Polling interval bounds (ms) for live ZeloScore updates.
// Randomised within this window so the "all scores update at once" pulse
// can't be precisely predicted — that unpredictability is the mystery hook.
const POLL_MIN_MS = 10000;
const POLL_MAX_MS = 20000;

const SubmissionsGrid = ({ submissions, deadline, competition, onDelete }) => {
  const isLive = competition?.competition_state === 'live';
  const competitionType = competition?.competition_type;
  const isCommunityScore = competitionType === 'community_score';

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [votes, setVotes] = useState({});
  const { user } = useAuth();

  const currentUserId = user?.id;
  const [submissionsList, setSubmissionsList] = useState(submissions);

  const { openModal } = useModal();

  const [likedSubmissions, setLikedSubmissions] = useState(() =>
    Object.fromEntries((submissions || []).map((s) => [s.id, s.is_liked_by_me ?? false]))
  );

  // 🔄 Sync local state when parent updates submissions
  useEffect(() => {
    setSubmissionsList(submissions || []);

    setLikedSubmissions(
      Object.fromEntries((submissions || []).map((s) => [s.id, s.is_liked_by_me ?? false]))
    );

    setVotes((prev) => {
      const next = { ...prev };
      (submissions || []).forEach((s) => {
        if (typeof s.votes_count === 'number') {
          next[s.id] = { count: s.votes_count, likedBy: [] };
        }
      });
      return next;
    });
  }, [submissions]);

  // ─────────────────────────────────────────────────────────────────────────
  // 🔁 ZeloScore polling — community_score competitions only.
  //
  // Fetches the lightweight [{id, zelo_score}] array on a randomised interval,
  // then merges the new scores into submissionsList. Only submissions whose
  // score actually changed trigger a state update, so the grid re-renders
  // minimally. Polling stops automatically when:
  //   - the competition isn't community_score
  //   - the competition is no longer live
  //   - the component unmounts (navigation away)
  // ─────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isCommunityScore || !isLive || !competition?.id) return;

    let timeoutId;
    let cancelled = false;

    const randomInterval = () =>
      Math.floor(Math.random() * (POLL_MAX_MS - POLL_MIN_MS + 1)) + POLL_MIN_MS;

    const poll = async () => {
      try {
        const scores = await fetchCompetitionScores(competition.id);
        if (cancelled || !Array.isArray(scores)) return;

        const scoreMap = new Map(scores.map((s) => [s.id, s.zelo_score]));

        setSubmissionsList((prev) => {
          let changed = false;
          const next = prev.map((sub) => {
            const newScore = scoreMap.get(sub.id);
            if (newScore !== undefined && newScore !== sub.zelo_score) {
              changed = true;
              return { ...sub, zelo_score: newScore };
            }
            return sub;
          });
          return changed ? next : prev; // no-op if nothing moved
        });
      } catch (err) {
        console.error('ZeloScore poll failed:', err);
      } finally {
        if (!cancelled) {
          timeoutId = setTimeout(poll, randomInterval());
        }
      }
    };

    // Kick off the first poll after an initial randomised delay
    timeoutId = setTimeout(poll, randomInterval());

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isCommunityScore, isLive, competition?.id]);

  const handleDelete = (id) => {
    setSubmissionsList((prev) => prev.filter((sub) => sub.id !== id));
    onDelete(id);
  };

  // 🔁 Toggle vote (optimistic + authoritative sync)
  const handleVote = async (submissionId, competitionId) => {
    if (currentUserId == null) return;

    const isCurrentlyLiked = likedSubmissions[submissionId] ?? false;
    const submissionVotes = votes[submissionId] ?? { count: 0, likedBy: [] };

    const optimisticCount = isCurrentlyLiked
      ? submissionVotes.count - 1
      : submissionVotes.count + 1;

    setVotes((prevVotes) => ({
      ...prevVotes,
      [submissionId]: {
        ...submissionVotes,
        count: optimisticCount,
      },
    }));

    setLikedSubmissions((prev) => ({
      ...prev,
      [submissionId]: !isCurrentlyLiked,
    }));

    try {
      const payload = {
        comp_id: competitionId,
        submission_id: submissionId,
      };
      const res = await addVote(payload);

      if (res && typeof res.count === 'number') {
        setVotes((prev) => ({
          ...prev,
          [submissionId]: {
            ...prev[submissionId],
            count: res.count,
          },
        }));
      }

      if (res && typeof res.user_voted === 'boolean') {
        setLikedSubmissions((prev) => ({
          ...prev,
          [submissionId]: res.user_voted,
        }));
      }
    } catch (error) {
      console.error('Failed to toggle like status', error);
      setVotes((prevVotes) => ({
        ...prevVotes,
        [submissionId]: submissionVotes,
      }));
      setLikedSubmissions((prev) => ({
        ...prev,
        [submissionId]: isCurrentlyLiked,
      }));
    }
  };

  // 🧠 After edit completes, update local list
  const handleEditSub = (updateSub) => {
    setSubmissionsList((prevList) =>
      prevList.map((submission) =>
        submission.id === updateSub.id ? { ...submission, ...updateSub } : submission
      )
    );
  };

  // 🗳️ Handle vote updates from modal (sync back to grid)
  const handleVoteUpdate = useCallback((submissionId, newUserVoted, newVoteCount) => {
    setLikedSubmissions((prev) => ({
      ...prev,
      [submissionId]: newUserVoted,
    }));

    setVotes((prev) => ({
      ...prev,
      [submissionId]: {
        ...(prev[submissionId] || { likedBy: [] }),
        count: newVoteCount,
      },
    }));
  }, []);

  // 📏 Responsive column count
  const columnCount = useMemo(() => {
    if (viewportWidth >= 1280) return GRID_ITEMS_PER_ROW.xl;
    if (viewportWidth >= 1024) return GRID_ITEMS_PER_ROW.lg;
    if (viewportWidth >= 768) return GRID_ITEMS_PER_ROW.md;
    if (viewportWidth >= 640) return GRID_ITEMS_PER_ROW.sm;
    return 1;
  }, [viewportWidth]);

  const rowCount = Math.ceil(submissionsList.length / columnCount);
  const parentRef = useRef();

  const rowHeightRef = useRef({});

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => rowHeightRef.current[index] ?? 320,
    overscan: 5,
    measureElement: (el) => el?.getBoundingClientRect().height ?? 320,
  });

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🧱 Render submission card
  const renderCard = useCallback(
    (submission) => {
      const isLiked = likedSubmissions[submission.id] ?? false;
      const voteData = votes[submission.id] ?? { count: submission.votes_count ?? 0 };
      const count = voteData.count;

      const author = submission.user || {};

      const display =
        author?.profile?.name?.trim() ||
        [author?.first_name, author?.last_name].filter(Boolean).join(' ').trim() ||
        author?.username ||
        `User ID: ${author?.id ?? 'unknown'}`;

      return (
        <SubmissionCard
          key={submission.id}
          submission={submission}
          isLiked={isLiked}
          votes={count}
          authorName={display}
          isLive={isLive}
          competitionType={competitionType}
          onLike={(e) => {
            e.stopPropagation();
            handleVote(submission.id, submission.competition);
          }}
          onClick={() =>
            openModal('SUBMISSION_PREVIEW', {
              submission: {
                ...submission,
                is_liked_by_me: likedSubmissions[submission.id],
                votes_count: votes[submission.id]?.count ?? submission.votes_count,
                // zelo_score travels with the submission object already
              },
              user: currentUserId,
              onDelete: handleDelete,
              onEdit: (editSubmission) =>
                openModal('EDIT_SUBMISSION', {
                  submission: editSubmission || submission,
                  user: currentUserId,
                  updateSubmissions: handleEditSub,
                }),
              onVoteUpdate: handleVoteUpdate,
            })
          }
        />
      );
    },
    [votes, likedSubmissions, currentUserId, handleVoteUpdate, competitionType, isLive]
  );

  return (
    <div className="text-white rounded-xl mx-auto">
      <div className="flex justify-between items-center px-6 mb-6">
        <h1 className="text-2xl font-bold text-mikado-yellow">
          {(() => {
            const state = competition?.competition_state;
            if (state === 'upcoming') return 'Voting Begins Soon! ⏳';
            if (state === 'live') return submissionsList?.length > 0
              ? 'Vote For Your Favourite(s)! 🚀'
              : 'No Submissions Yet! 🦗';
            return 'Competition Completed! 🏆';
          })()}
        </h1>
        <span className="text-red-500 font-medium">
          <Countdown deadline={deadline} />
        </span>
      </div>

      <div ref={parentRef} className="h-[800px] overflow-auto px-6">
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const startIndex = virtualRow.index * columnCount;
            const rowSubmissions = submissionsList.slice(
              startIndex,
              startIndex + columnCount
            );

            return (
              <div
                key={virtualRow.index}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-2 gap-y-3 pb-3"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                {rowSubmissions.map(renderCard)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SubmissionsGrid;